<template>
  <div class="vb-sidebar">
    <div v-if="loading">
    </div>
    <div v-else class="vb-sidebar__elements">
      <b-row>
        <b-col>
          <slot name="resume" />
        </b-col>
      </b-row>

      <b-row v-if="external_condition_lock">
        <b-col>
          <sidebar-external-message v-for="(external_condition, index) in external_checkout_conditions.filter(c => { return c.sidebar_validation == false })" :external_condition="external_condition" v-bind:key="_uid+index" />
        </b-col>
      </b-row>

      <b-row class="vb-sidebar__messages">
        <b-col>
          <slot name="message">
            <div v-if="completed" class="vb-sidebar__block--completed">
              Acquisto completato!<!--NB: lo slot di default non tiene conto dell'eventuale errore, che va gestito dal parent-->
            </div>
            <div v-if="!completed && purchasing" class="text-center">
              <loader>
                <template>
                  <span>Prenotazione in corso... non abbandonare o ricaricare la pagina!</span>
                </template>
              </loader>
            </div>
          </slot>                    
        </b-col>
      </b-row>

      <b-row :class="{ 'vb-sidebar--locked': external_condition_lock }">
        <b-col>
          <div v-if="!completed">
            <div v-if="!purchasing">
              <div v-for="(block,key) in sidebar_blocks" v-bind:key="_uid+key">
                <component
                  v-if="block.show"
                  @change="event => sidebar_block_update(key, event) /*ALL*/"
                  :validate_on_mount="validate_on_mount"
                  :class="'vb-sidebar__block--' + key + ' ' + (block.payload === false ? 'invalid' : 'valid')"
                  :ref="key /*ALL -> serve al callback di stripe*/"
                  :is="key /*ALL*/"
                  :live_price="live_price /*discount_codes*/"
                  :av_from_state="availability.details.integration /*discount_codes*/"
                  :dc_from_state="discount_codes /*discount_codes*/"
                  :payments="payments  /*payments*/"
                  :credit_card_amount="credit_card_amount /*stripe*/"
                />
              </div>
            </div>

            <div v-if="!purchasing">
              <div class="vb-sidebar__block--btn" v-if="has_slot('before_purchase')">
                <slot name="before_purchase" />
              </div>
            </div>
            
            <div class="vb-sidebar__block--btn" v-if="!purchasing">
              <h5 class="vb-heading">Procedi con l'acquisto</h5>
              <vb-button
                @click="handle_sidebar_purchase()"
                :vb_options="{
                  text: 'Prenota',
                  disabled: !disable_checkout_button(),
                  icon_disabled: 'lock'
                }"
              />
              <span class="d-block text text--sm mt-2" v-if="!disable_checkout_button()" v-html="'Verifica di aver compilato tutti i campi correttamente e di avere accettato le condizioni d\'uso e di trattamento dei dati.'" />
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import SidebarGuest from './sidebar-guest'
import SidebarBooker from './sidebar-booker'
import SidebarDiscountCodes from './sidebar-discount-codes'
import SidebarPayments from './sidebar-payments'
import SidebarBillToPax from './sidebar-bill-to-pax'
import SidebarStripe from './sidebar-stripe'
import SidebarOtp from './sidebar-otp'
import SidebarExternalMessage from './sidebar-external-message'
import { get_config } from '@utilities/commons'
import { get_blocks_configuration, should_show_stripe_block, should_show_bill_to_pax_block, should_show_discount_codes_block } from './logic/blocks-visibility'
import { calculate_credit_card_amount, calculate_payments, calculate_payments_sum } from './logic/payments'
import { should_lock_sidebar, can_proceed_to_checkout } from './logic/validation'
import { prepare_booking_payments_data, process_stripe_event, extract_discount_codes_data } from './logic/checkout'

export default {
  name: "sidebar",

  components: {
    'guest': SidebarGuest,
    'discount_codes': SidebarDiscountCodes,
    'payments': SidebarPayments,
    'stripe': SidebarStripe,
    'otp': SidebarOtp,
    'bill_to_pax': SidebarBillToPax,
    'booker': SidebarBooker,
    'sidebar-external-message': SidebarExternalMessage,
  },

  props: {
    validate_on_mount: {
      type: Boolean,
      default: false,
    },
    points: {
      type: Number,
      default: 0
    },
    external_checkout_conditions: {
      type: Array,
      default: () => {
        return []
      },
    },
    live_price: String,
    loading: Boolean,
    purchasing: Boolean,
    completed: Boolean,
    discount_codes: Array,
    integration: Object,
    availability: [Object],
  },

  data() {
    return {
      booking_data_payments: {
        welfare: {},
        discountCodes: {},
        cc: {},
      },
      sidebar_blocks: {},
      bill_to_pax_required: this.integration.billToPax,
      booker_required: (get_config('app') === 'livebk' && !this.integration.billToPax),
      otp_required: (this.integration && this.integration.otpRequired) ? this.integration.otpRequired : false,
      otp_check: false,
      stripe_completed: false,
      discount_codes_amount: 0,
    }
  },

  watch: {
    // NB: unico modo per gestire il flag "show" di stripe coerentemente al residuo lasciato dai codici sconto
    credit_card_amount() {
      this.sidebar_blocks.stripe.show = (this.credit_card_amount > 0)

      // Ricalcola visibilità bill_to_pax quando cambia credit_card_amount
      if (this.sidebar_blocks.bill_to_pax) {
        this.sidebar_blocks.bill_to_pax.show = should_show_bill_to_pax_block({
          app_name: get_config('app'),
          bill_to_pax_required: this.bill_to_pax_required,
          welfare: this.integration.welfare || false,
          credit_card_amount: this.credit_card_amount,
          discount_codes_amount: this.discount_codes_amount,
          points: this.points
        })
      }
    },

    // Ricalcola visibilità discount_codes quando cambiano i points
    points() {
      if (this.sidebar_blocks.discount_codes) {
        this.sidebar_blocks.discount_codes.show = should_show_discount_codes_block({
          welfare: this.integration.welfare || false,
          points: this.points,
          has_login_discount_codes: !!this.integration.loginDiscountCode,
          app_name: get_config('app')
        })
      }
    },
  },

  computed: {
    /**
     * Validation Module
     */
    external_condition_lock() {
      return should_lock_sidebar(this.external_checkout_conditions)
    },

    /**
     * Payments Module
     */
    payments() {
      return calculate_payments({
        live_price: this.live_price,
        integration_credit: this.availability.details.integration,
        discount_codes_amount: this.discount_codes_amount,
        credit_card_amount: this.credit_card_amount,
        points: this.points,
        points_config: this.availability.points,
      })
    },

    credit_card_amount() {
      return calculate_credit_card_amount({
        live_price: this.live_price,
        integration_credit: this.availability.details.integration,
        discount_codes_amount: this.discount_codes_amount,
        is_welfare: this.integration.welfare,
      })
    },

    payments_sum() {
      return calculate_payments_sum(this.payments)
    },
  },

  created() {
    this.get_config = get_config
  },

  mounted() {
    this.sidebar_blocks = get_blocks_configuration({
      points: this.points,
      credit_card_amount: this.credit_card_amount,
      integration: this.integration,
      app_name: get_config('app'),
      otp_required: this.otp_required,
      otp_check: this.otp_check,
      stripe_completed: this.stripe_completed,
      discount_codes_amount: this.discount_codes_amount,
    })
  },

  methods: {
    has_slot(slot_name) {
      return !!this.$slots[slot_name]
    },

    handle_stripe_update(event) {
      const result = process_stripe_event(event, {
        otp_required: this.otp_required,
        otp_check: this.otp_check,
        credit_card_amount: this.credit_card_amount,
      })

      // Se nessuna azione richiesta (es. stripe-change), esci
      if (!result) return

      // Aggiorna dati carta
      if (result.cc_data !== undefined) {
        if (result.cc_data === null) {
          // Errore: non aggiornare i dati
        } else {
          this.booking_data_payments.cc = {
            ...this.booking_data_payments.cc,
            ...result.cc_data
          }
        }
      }

      // Se stripe-init, reset completo
      if (event.name === 'stripe-init') {
        this.booking_data_payments.cc = {}
      }

      // Aggiorna stato stripe
      this.stripe_completed = result.stripe_completed

      // Aggiorna visibilità OTP
      this.sidebar_blocks.otp.show = result.should_show_otp

      // Salva payload stripe in sidebar_blocks
      if (result.stripe_block_payload) {
        this.sidebar_blocks.stripe = result.stripe_block_payload
      }

      // Trigger OTP se necessario (aspetta nextTick per il DOM)
      if (result.should_trigger_otp) {
        this.$nextTick(() => {
          /**
           * nextTick() attende l'aggiornamento del DOM, serve altrimenti non trovo le $refs aggiornate
           * ed <otp /> in questo momento potrebbe non essere ancora montato nel dom)
           */
          if (this.$refs.otp && this.$refs.otp[0]) {
            this.$refs.otp[0].trigger()
          }
        })
      }
    },

    handle_discount_codes_update(event) {
      const data = extract_discount_codes_data(event)
      this.discount_codes_amount = data.amount
      this.$emit('commit', {
        name: 'discount_codes',
        payload: data.codes
      })
    },

    handle_sidebar_purchase() {
      /**
       * controllo ridondante:
       * teoricamente se c'è qualche errore o campo mancante il tasto "Prenota" è disabilitato
       * MA: nel caso in cui si reintroducesse l'automatismo tra l'ok di stripe e la prenotazione, allora
       * questo controllo diventa importante perché il pulsante viene bypassato e si chiama direttamente il metodo
       */
      if (this.disable_checkout_button() == false) return

      // Nascondi blocco OTP
      this.sidebar_blocks.otp.show = false

      // Prepara i dati di pagamento usando la funzione pura
      this.booking_data_payments = prepare_booking_payments_data({
        live_price: this.live_price,
        discount_codes_amount: this.discount_codes_amount,
        discount_codes: this.discount_codes,
        credit_card_amount: this.credit_card_amount,
        integration: this.integration,
        existing_cc_data: {
          paymentIntentId: this.booking_data_payments.cc.paymentIntentId,
          nameSurname: this.booking_data_payments.cc.nameSurname,
        }
      })

      // Emetti evento di acquisto
      this.$nextTick(() => {
        this.$emit('purchase', {
          bill_to_pax: this.sidebar_blocks.bill_to_pax,
          booker: this.sidebar_blocks.booker,
          guest: this.sidebar_blocks.guest,
          payments: this.booking_data_payments,
        })
      })
    },

    sidebar_block_update(key, event) {
      this.log('[sidebar_key_change] ' + key + ' - ' + event.name)
      this.sidebar_blocks[key].payload = event.payload
      switch (key) {
        case 'guest':
          /**
           * non più necessario. prima era: this.sidebar_blocks[key].payload = event.payload
           */
          break

        case 'bill_to_pax':
          /**
           * non più necessario. prima era: this.sidebar_blocks[key].payload = event.payload
           */
          break

        case 'discount_codes':
          this.handle_discount_codes_update(event)
          break

        case 'stripe':
          this.handle_stripe_update(event)
          break

        case 'otp':
          this.otp_check = event.payload
          if (event.name == 'otp-purchase') {
            if (this.stripe_completed == false && should_show_stripe_block(this.credit_card_amount)) return
            /**
             * this.handle_sidebar_purchase() => NO:
             * altrimenti nel caso in cui stripe non serve e l'utente ha i dati precompilati dal browsersi innesca
             * un automatismo che fa partire l'acquisto senza nessun click di conferma
             */
            this.sidebar_blocks.otp.show = false
          }
          break

        default:
          break
      }
    },

    disable_checkout_button() {
      return can_proceed_to_checkout({
        sidebar_blocks: this.sidebar_blocks,
        external_conditions: this.external_checkout_conditions,
        bill_to_pax_required: this.bill_to_pax_required,
        booker_required: this.booker_required,
        otp_required: this.otp_required,
        otp_check: this.otp_check,
        credit_card_amount: this.credit_card_amount,
        stripe_completed: this.stripe_completed,
        is_availability_known: this.availability.is_known,
        live_price: this.live_price,
        payments_sum: this.payments_sum,
      })
    },
  },

}
</script>

<style lang="scss" scoped>

.vb-sidebar {
  &--locked {
    opacity: 0.5;
    pointer-events: none;
  }
  top: 10px;

  &__elements {
    @include media-breakpoint-down(sm) {
      display: flex;
      flex-direction: column;
      &>* {
        order: 2;
      }
    }
  }

  &__messages {
    @include media-breakpoint-down(sm) {
      order: 1;
      margin-bottom: 2rem;
    }    
  }

  &__block {
    background:$white;
    padding: 20px;
    margin-bottom: 20px;
    border-radius: $border-radius-lg * 2;

    .vb-heading {
      display:block;
      margin-bottom: 1.25rem;
    }

    &.valid {}

    &.invalid {}

    &--completed {
      @extend .vb-sidebar__block;
      p {
        text-align: center;
      }
    }

    &--guest {
      @extend .vb-sidebar__block;
    }
    
    &--bill_to_pax {
      @extend .vb-sidebar__block;
    }

    &--booker {
      @extend .vb-sidebar__block;
    }

    &--discount_codes {
      @extend .vb-sidebar__block;
    }

    &--payments {
      @extend .vb-sidebar__block;
    }
    
    &--stripe {
      @extend .vb-sidebar__block;
    }

    &--otp {
      @extend .vb-sidebar__block;
    }

    &--external-message {
      @extend .vb-sidebar__block;
    }

    &--btn {
      @extend .vb-sidebar__block;
      p {
        text-align: center;
      }
      .btn {
        width:100%;
      }
    }

    &--disclaimer {
      //@extend .vb-sidebar__block;
      p {
        text-align: left;
      }
    }
  }
}
</style>